import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import {
  AGGREGATION_TYPE,
  GRAPH_DATA_TYPE,
  GRAPH_PERIODICITY,
} from '../../../../../utils/enums/generic.enum';
import { LmeTotalCarbonReductionStyles as styles } from './lme-total-carbon-reduction.styles';
import { LmeTotalCarbonReductionTemplate as template } from './lme-total-carbon-reduction.template';
import Highcharts from 'highcharts';
import { IProjectForecastTs, IProjectHistoricalTs } from '../../../../../types';
import { getDatesFromData, getValuesFromData } from '../../../../../utils/chart-data-utils';

const name = 'lme-total-carbon-reduction';

@customElement({
  name,
  template,
  styles,
})
export class LmeTotalCarbonReduction extends FASTElement {
  @observable historicalData: Array<IProjectHistoricalTs>;
  @observable forecastData: Array<IProjectForecastTs>;

  @observable lmeChart!: HTMLDivElement;
  @observable dataType: GRAPH_DATA_TYPE = GRAPH_DATA_TYPE.FORECAST;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;

  @observable isVisible: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    this.renderLmeTotalCarbonReduction();
  }

  public renderGraph(
    historicalData: Array<IProjectHistoricalTs>,
    forecastData: Array<IProjectForecastTs>
  ) {
    if (historicalData) {
      // Get a date object for the current time
      const minimumDate = new Date();

      // Set it to three years ago
      minimumDate.setFullYear(minimumDate.getFullYear() - 3);
      historicalData = historicalData.filter((x) => x.METRICS_DATETIME > minimumDate.getTime());
    }

    this.historicalData = historicalData ? historicalData.slice() : [];
    this.forecastData = forecastData ? forecastData.slice() : [];
    this.renderLmeTotalCarbonReduction();
  }

  public dataTypeChanged(oldValue, newValue) {
    this.renderLmeTotalCarbonReduction();
  }

  public periodicityChanged(oldValue, newValue) {
    this.renderLmeTotalCarbonReduction();
  }

  public reflow() {
    this.isVisible = false;
    setTimeout(() => {
      this.renderLmeTotalCarbonReduction();
    }, 1);
  }

  private renderLmeTotalCarbonReduction() {
    if (
      this.dataType === GRAPH_DATA_TYPE.HISTORICAL &&
      (!this.historicalData || this.historicalData.length === 0)
    ) {
      this.isVisible = false;
      return;
    }

    if (
      this.dataType === GRAPH_DATA_TYPE.FORECAST &&
      (!this.forecastData || this.forecastData.length === 0)
    ) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;

    const dates = getDatesFromData(
      this.dataType === GRAPH_DATA_TYPE.FORECAST ? this.forecastData : this.historicalData,
      this.periodicity
    );
    const lmeAsgen =
      this.dataType === GRAPH_DATA_TYPE.HISTORICAL
        ? getValuesFromData(
            this.historicalData,
            'LME_ASGEN_NODE_MT_CO2E_PER_MWH',
            this.periodicity,
            this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
          )
        : getValuesFromData(
            this.forecastData,
            'LME_ASGEN_HUB_MT_CO2E_PER_MWH',
            this.periodicity,
            this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
          );
    const mtCcarbonAbated =
      this.dataType === GRAPH_DATA_TYPE.HISTORICAL
        ? getValuesFromData(
            this.historicalData,
            'CARBON_ABATED_NODE_MT_CO2E',
            this.periodicity,
            this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
          )
        : getValuesFromData(
            this.forecastData,
            'CARBON_ABATED_EXPECTED_MT_CO2E',
            this.periodicity,
            this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
          );

    Highcharts.chart({
      chart: {
        renderTo: this.lmeChart,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: dates,
      },
      yAxis: [
        {
          // Primary yAxis
          title: {
            text: 'MT CO₂/MWh',
          },
          labels: {
            format: '{value:,.2f}',
          },
        },
        {
          // Secondary yAxis
          title: {
            text: 'MT CO₂',
            rotation: -90,
            margin: 20,
          },
          labels: {
            format: '{value:,.2f}',
          },
          opposite: true,
        },
      ],
      tooltip: {
        valueDecimals: 2,
      },
      series: [
        {
          type: 'line',
          color: Highcharts.getOptions().colors[6],
          yAxis: 0,
          name: this.getLineName(),
          data: lmeAsgen,
          zIndex: 2,
        },
        {
          type: 'column',
          color: Highcharts.getOptions().colors[2],
          yAxis: 1,
          name: this.getAreaName(),
          data: mtCcarbonAbated,
          zIndex: 1,
        },
      ],
    });
  }

  private getAreaName(): string {
    return this.dataType === GRAPH_DATA_TYPE.FORECAST
      ? 'Expected Total Zonal Carbon Avoided (MT CO₂)'
      : 'Total Nodal Carbon Avoided - Historical (MT CO₂)';
  }

  private getLineName(): string {
    return this.dataType === GRAPH_DATA_TYPE.FORECAST
      ? 'Zonal LME - Forecast (MT CO₂/MWh)'
      : 'Nodal LME Generation Weighted - Historical (MT CO₂/MWh)';
  }
}
